import { createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { queryClient } from '@/services/queryClient';
import LoadingPage from '@/components/templates/LoadingPage';

/**
 * The router instance, kept outside App so non-React code can reach it.
 *
 * The response interceptor needs to navigate when a session ends, and calling
 * `redirect()` outside a route lifecycle does nothing — it only builds a
 * redirect object for the router to act on when thrown from `beforeLoad`. A
 * module-level instance gives that code a real `router.navigate` to call.
 */
export const router = createRouter({
  routeTree: routeTree,
  context: {
    auth: null,
    queryClient,
  },
  // Declared here only. RouterProvider also accepts defaultPreload, and both apps
  // set it in both places — so a change to one was silently overridden by the
  // other.
  //
  // Kept as 'intent' rather than scoped away from authenticated routes. Hovering
  // a link runs its loader, which used to be a hazard during sign-out: the
  // teardown cleared the query cache while the authenticated tree was still
  // mounted, cancelling in-flight loaders whose rejections then replaced the app
  // with the error page. Sign-out now navigates before it clears, so those
  // rejections land on a tree that is already unmounting, and the route-level
  // boundaries contain what is left. Losing preloading would be a real
  // regression to fix a hazard that no longer exists.
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => <LoadingPage />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
